import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { hasPermission, type Action, type Permission, type Resource } from '@/lib/permissions';
import type { MatterDetail } from '@/types';

export interface CurrentMatterContextValue {
  matter: MatterDetail;
  mutate: () => Promise<MatterDetail | undefined>;
}

export interface CurrentMatter {
  matter: MatterDetail;
  permissions: Permission[];
  canAccess: (resource: Resource, actions: Action[]) => boolean;
  refresh: () => Promise<MatterDetail | undefined>;
}

// `null` sentinel so consumers outside the provider fall back / throw on access.
const CurrentMatterContext = createContext<CurrentMatterContextValue | null>(null);

/**
 * Holds the matter resolved by `MatterAccessGuard`. The guard only renders this
 * provider once access is granted and the matter is loaded, so every route under
 * `/matter/$slug` can read a guaranteed-present matter with no loading/error
 * handling of its own.
 */
export function CurrentMatterProvider({
  value,
  children,
}: {
  value: CurrentMatterContextValue;
  children: ReactNode;
}) {
  return (
    <CurrentMatterContext.Provider value={value}>{children}</CurrentMatterContext.Provider>
  );
}

/**
 * Single source of truth for the current matter and the caller's access to it.
 * Available to any component under `MatterAccessGuard`; throws if used outside
 * the provider (i.e. outside a `/matter/$slug` route).
 *
 * Exposes the matter plus its role/permissions and a `canAccess` helper, so
 * consumers don't need separate permission hooks.
 */
export function useCurrentMatter(): CurrentMatter {
  const ctx = useContext(CurrentMatterContext);
  const permissions = ctx?.matter.permissions ?? [];

  const canAccess = useCallback(
    (resource: Resource, actions: Action[]) => hasPermission(permissions, resource, actions),
    [permissions],
  );

  if (!ctx) {
    throw new Error('useCurrentMatter must be used within a CurrentMatterProvider');
  }

  return {
    matter: ctx.matter,
    permissions: ctx.matter.permissions,
    canAccess,
    refresh: ctx.mutate,
  };
}
